export type JsonPrimitive = string | number | boolean | null;

export type JsonValue =
  | JsonPrimitive
  | JsonValue[]
  | { [key: string]: JsonValue };

export type UserProfileImage = {
  url: string | null;
  storage: string | null;
  path: string | null;
  file_name: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_at: string | null;
};

export type UserSettingsState = {
  preferences?: Record<string, JsonValue>;
};

export type User = {
  readonly id: number;
  readonly user_id: string;
  name: string;
  nickname?: string | null;
  display_name?: string | null;
  email: string;
  phone_number?: string | null;
  bio?: string | null;
  status?: string | null;
  initials?: string | null;
  avatar_url?: string | null;
  avatar_fallback_url?: string | null;
  profile_image_url?: string | null;
  profile_image?: UserProfileImage | null;
  settings?: UserSettingsState | null;
  password?: string;
  readonly created_at: string;
  readonly updated_at: string;
};

export type SettingsTabId = 'profile' | 'account' | 'security' | 'preferences';

export type SettingsPayload = {
  user: User;
  settings: UserSettingsState;
};

export type UpdateProfileRequest = {
  name: string;
  nickname?: string | null;
  email: string;
  phone_number?: string | null;
  bio?: string | null;
};

export const DEFAULT_AVATAR_URL = '/assets/user-avatar.svg';

export function resolveUserDisplayName(
  user: Pick<User, 'display_name' | 'nickname' | 'name'> | null | undefined
): string {
  const displayName = normalizeUserText(user?.display_name);
  if (displayName !== null) {
    return displayName;
  }

  const nickname = normalizeUserText(user?.nickname);
  if (nickname !== null) {
    return nickname;
  }

  return normalizeUserText(user?.name) ?? 'User';
}

export function resolveUserGivenName(
  user: Pick<User, 'display_name' | 'nickname' | 'name'> | null | undefined
): string {
  const displayName = resolveUserDisplayName(user);
  const segments = displayName.split(/\s+/).filter((segment) => segment.length > 0);
  return segments[0] ?? 'User';
}

export function resolveUserInitials(
  user: Pick<User, 'initials' | 'display_name' | 'nickname' | 'name'> | null | undefined,
  fallback = 'VL'
): string {
  const storedInitials = normalizeUserText(user?.initials);
  if (storedInitials !== null) {
    return storedInitials;
  }

  const baseName = resolveUserDisplayName(user);
  const segments = baseName.split(/\s+/).filter((segment) => segment.length > 0);
  const initials = segments
    .slice(0, 2)
    .map((segment) => segment.slice(0, 1))
    .join('')
    .toUpperCase();

  return initials || fallback;
}

export function resolveUserAvatarUrl(
  user: User | null | undefined,
  fallbackUrl = DEFAULT_AVATAR_URL
): string {
  const candidates = [
    user?.profile_image?.url,
    user?.profile_image_url,
    user?.avatar_url,
    user?.avatar_fallback_url,
  ];

  const resolved = candidates.find(
    (candidate): candidate is string =>
      typeof candidate === 'string' && candidate.trim().length > 0
  );

  return resolved ?? fallbackUrl;
}

export type LoginRequest = {
  email: string;
  password: string;
  rememberMe?: boolean;
};

export type LoginResponse = {
  success: boolean;
  message: string;
  data: {
    user: User;
    access_token: string;
    refresh_token: string;
    remember_token?: string;
    expires_in: number;
  };
  meta?: JsonValue[];
};

export type RegisterRequest = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreed_to_terms: boolean;
};

export type RegisterResponse = {
  success: boolean;
  message: string;
  data?: {
    user: User;
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
  errors?: Record<string, string[]>;
  meta?: JsonValue[];
};

export type PasswordResetRequest = {
  email: string;
};

export type OtpVerificationRequest = {
  email: string;
  otp: string;
};

export type PasswordUpdateRequest = {
  email: string;
  otp: string;
  password: string;
};

export type ApiResponse<T = never> = {
  success: boolean;
  message?: string;
  data?: T;
  errors?: Record<string, string[]>;
  meta?: JsonValue[];
};

function normalizeUserText(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();
  return normalizedValue.length > 0 ? normalizedValue : null;
}
